import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useFadeIn } from '../components/useFadeIn';
import CarbonCredit from '../Images/carboncredit.jpg';
import AiDriven from '../Images/aidriven.jpg';
import RealTime from '../Images/realtime.jpg';
import Benchmarking from '../Images/benchmarking.jpg';
import Sustainability from '../Images/sustainability.jpg';
import HomeHero from '../Images/homehero.jpg';
import Consulting from '../Images/consulting.jpg';
import Innovation from '../Images/innovation.jpg';

const challenges = [
  { title: 'Fragmented & Inaccurate Data', desc: 'Supply chain emissions are hard to measure, making sustainability efforts reactive instead of proactive.' },
  { title: 'Outdated & Manual Reporting', desc: 'Traditional methods rely on historical data, delaying real-time insights needed for optimization.' },
  { title: 'Regulatory & Cost Pressures', desc: 'Stricter ESG regulations and rising carbon costs demand smarter, AI-driven compliance solutions.' },
  { title: 'Missed Financial Opportunities', desc: 'Companies fail to monetize carbon reductions due to a lack of integrated carbon credit trading tools.' },
];

const platformTabs = [
  {
    label: 'Sustainability Dashboard',
    title: 'Sustainability Command Center',
    desc: 'Our intuitive dashboard brings your emissions data to life, providing actionable insights at a glance and putting powerful analysis tools at your fingertips.',
    image: Sustainability,
    points: [
      'Real-Time Status Indicators that visualize compliance and performance using simple color coding',
      'Process Optimization Tools that identify inefficiencies and suggest targeted improvements',
      'Predictive Analysis & Data Modeling to forecast impacts of operational changes',
      'Carbon Credit Optimization Portal for monitoring, verification, and trading',
    ],
  },
  {
    label: 'Consulting & Training',
    title: 'Expert Guidance & Capacity Building',
    desc: "Technology alone isn't enough. Our team of sustainability experts works alongside yours to build internal capabilities and accelerate your transition.",
    image: Consulting,
    points: [
      'Strategic Sustainability Planning tailored to your industry and goals',
      'Comprehensive Training Programs for technical teams and leadership',
      'Carbon Credit Optimization to maximize returns on emissions reduction',
      'Compliance Strategy Development across multiple jurisdictions',
    ],
  },
  {
    label: 'Innovation Lab',
    title: 'Future-Forward Technology',
    desc: 'Our innovation lab develops cutting-edge solutions that keep you ahead of the sustainability curve.',
    image: Innovation,
    points: [
      'Advanced Carbon Market Access platforms for organizations of all sizes',
      'AI-Powered Investment Analysis for sustainability initiatives',
      'Real-Time ESG Performance Tracking integrated with financial metrics',
      'Predictive Carbon Pricing Models for strategic planning',
    ],
  },
];

const howItWorks = [
  { num: '01.', title: 'Onboarding & Setup', desc: 'Customize settings based on industry and region.' },
  { num: '02.', title: 'Data Integration', desc: 'Connect to ERPs for automated emissions tracking.' },
  { num: '03.', title: 'Regulatory Compliance', desc: 'Identify and manage applicable sustainability laws.' },
  { num: '04.', title: 'AI-Driven Recommendations', desc: 'Optimize sustainability with smart insights.' },
  { num: '05.', title: 'Carbon Credit Trading', desc: 'Buy, sell, and trade verified credits in real time.' },
];

const serviceCards = [
  {
    title: 'Carbon Credit Management & Trading',
    desc: 'Track, verify, and monetize emissions reductions through integrated carbon credit marketplaces, turning sustainability into a financial advantage.',
    image: CarbonCredit,
  },
  {
    title: 'Real-Time Emissions Tracking',
    desc: 'Monitor and optimize carbon footprints across operations and supply chains with live, automated data instead of outdated reports.',
    image: RealTime,
  },
  {
    title: 'AI-Driven Predictive Analysis',
    desc: 'Leverage advanced modeling to forecast the impact of operational changes, equipment upgrades, and supply chain decisions—cutting emissions by up to 20%.',
    image: AiDriven,
  },
  {
    title: 'Benchmarking & Compliances',
    desc: 'Compare emissions against industry standards, generate audit-ready reports, and ensure compliance with global ESG regulations like TCFD, CSSB, and EU ETS.',
    image: Benchmarking,
  },
];

function FadeSection({ children, className = '' }) {
  const ref = useFadeIn();
  return <div ref={ref} className={`section-fade ${className}`}>{children}</div>;
}

export default function Home() {
  const [activeTab, setActiveTab] = useState(0);
  const [tabVisible, setTabVisible] = useState(true);
  const [heroVisible, setHeroVisible] = useState(false);
  const tab = platformTabs[activeTab];

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleTabChange = (i) => {
    if (i === activeTab) return;
    setTabVisible(false);
    setTimeout(() => {
      setActiveTab(i);
      setTabVisible(true);
    }, 300);
  };

  return (
    <main>
      {/* Hero */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-4 sm:px-6 overflow-hidden">
        <div
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{ opacity: heroVisible ? 1 : 0 }}
        >
          <img
            src={HomeHero}
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2d3828]/60 to-[#2d3828]/80" />
        </div>

        <div
          className="relative z-10 max-w-4xl mx-auto transition-all duration-1000 ease-in-out"
          style={{
            opacity: heroVisible ? 1 : 0,
            transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
          }}
        >
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 sm:mb-6">
            Transform Your Emissions Data into<br className="hidden sm:block" /> Actionable Intelligence.
          </h1>
          <p className="font-body text-white/70 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 sm:mb-10">
            Carbonetics empowers businesses with AI-driven sustainability insights, ensuring compliance, reducing emissions, and unlocking new revenue opportunities.
          </p>
          <Link to="/contact"
            className="inline-flex items-center gap-2 border border-white text-white font-body px-6 sm:px-8 py-3 sm:py-3.5 rounded-full hover:bg-white hover:text-[#2d3828] transition-all duration-300 text-sm sm:text-base">
            Request a demo
          </Link>
        </div>
      </section>

      {/* The Challenge */}
      <section className="bg-[#f4f7f0] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <FadeSection>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#1e3019] mb-4 sm:mb-6">The Challenge</h2>
            <p className="font-body text-[#1e3019]/70 text-base sm:text-lg max-w-lg mb-10 sm:mb-16">
              Businesses face mounting pressure to track emissions accurately, yet outdated methods create inefficiencies, compliance risks, and lost financial opportunities.
            </p>
          </FadeSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-12">
            {challenges.map((c, i) => (
              <FadeSection key={i}>
                <div className="flex flex-col gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#2d3828]/10 flex items-center justify-center mb-2">
                    <div className="w-4 h-4 rounded-full bg-[#2d3828]/30" />
                  </div>
                  <h3 className="font-body font-semibold text-[#1e3019] text-base sm:text-lg">{c.title}</h3>
                  <p className="font-body text-[#1e3019]/60 text-sm leading-relaxed">{c.desc}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section className="bg-[#2d3828] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <FadeSection>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">The Solution</h2>
            <p className="font-body text-white/70 text-base sm:text-lg max-w-lg">
              Carbonetics doesn't just track emissions, it transforms sustainability into a competitive advantage.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* Services Highlights with Images */}
      <section className="bg-[#2d3828] pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10">
          {serviceCards.map((s, i) => (
            <FadeSection key={i}>
              <div className="bg-[#364230] rounded-2xl overflow-hidden">
                <div className="p-6 sm:p-10">
                  <h3 className="font-body font-semibold text-white text-lg sm:text-xl mb-3">{s.title}</h3>
                  <p className="font-body text-white/60 text-sm leading-relaxed">{s.desc}</p>
                </div>
                <div className="px-6 sm:px-10 pb-6 sm:pb-10">
                  <div className="rounded-xl overflow-hidden h-64 sm:h-96">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
              </div>
            </FadeSection>
          ))}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-[#2d3828] py-16 sm:py-20 px-4 sm:px-6 text-center border-t border-white/10">
        <FadeSection>
          <p className="font-body text-white/80 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-6 sm:mb-8">
            With Carbonetics, businesses don't just meet regulations—they gain a competitive edge in the race to net-zero.
          </p>
          <Link to="/contact"
            className="inline-flex items-center gap-2 border border-white text-white font-body px-6 sm:px-8 py-3 sm:py-3.5 rounded-full hover:bg-white hover:text-[#2d3828] transition-all duration-300 text-sm sm:text-base">
            See Carbonetics in Action <ArrowRight size={16} />
          </Link>
        </FadeSection>
      </section>

      {/* Our Platform */}
      <section className="bg-[#2d3828] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <FadeSection>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">Our Platform</h2>
            <p className="font-body text-white/60 text-base sm:text-lg max-w-lg mb-8 sm:mb-12">
              Turn sustainability challenges into competitive advantages with our all-in-one platform.
            </p>
          </FadeSection>

          {/* Tab Switcher */}
          <div className="grid grid-cols-3 gap-1 sm:gap-2 mb-8 sm:mb-12 border border-white/10 rounded-xl overflow-hidden">
            {platformTabs.map((t, i) => (
              <button key={i} onClick={() => handleTabChange(i)}
                className={`font-body text-xs sm:text-sm py-3 sm:py-4 px-2 sm:px-3 transition-all duration-300 ${
                  activeTab === i ? 'bg-[#364230] text-white font-semibold' : 'text-white/50 hover:text-white/80'}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab Content with smooth transition */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center transition-all duration-300 ease-in-out"
            style={{
              opacity: tabVisible ? 1 : 0,
              transform: tabVisible ? 'translateY(0)' : 'translateY(10px)',
            }}
          >
            <div className="rounded-2xl overflow-hidden h-48 sm:h-72 order-2 md:order-1">
              <img
                src={tab.image}
                alt={tab.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="order-1 md:order-2">
              <h3 className="font-display text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">{tab.title}</h3>
              <p className="font-body text-white/60 text-sm leading-relaxed mb-6 sm:mb-8">{tab.desc}</p>
              <div className="flex flex-col gap-0">
                {tab.points.map((p, i) => (
                  <div key={i} className="flex gap-3 sm:gap-4 py-3 sm:py-4 border-b border-white/10">
                    <span className="font-mono text-white/30 text-xs sm:text-sm shrink-0">0{i + 1}.</span>
                    <span className="font-body text-white/80 text-xs sm:text-sm font-medium">{p}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact"
                className="inline-flex items-center gap-2 mt-6 sm:mt-8 border border-white text-white font-body px-5 sm:px-6 py-2.5 sm:py-3 rounded-full hover:bg-white hover:text-[#2d3828] transition-all text-xs sm:text-sm">
                Request A Demo <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[#f4f7f0] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <FadeSection>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 sm:gap-16">
              <div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1e3019] mb-4 sm:mb-6">How Carbonetics Works</h2>
                <p className="font-body text-[#1e3019]/60 text-sm sm:text-base leading-relaxed">
                  Carbonetics isn't just another emissions tracker - it's an intelligent sustainability engine that simplifies data collection, compliance, optimization, and monetization.
                </p>
              </div>
              <div className="flex flex-col gap-0">
                {howItWorks.map((step, i) => (
                  <div key={i} className="flex gap-4 sm:gap-6 py-4 sm:py-5 border-b border-[#1e3019]/10">
                    <span className="font-mono text-[#1e3019]/30 text-xs sm:text-sm shrink-0 pt-0.5">{step.num}</span>
                    <div>
                      <p className="font-body font-semibold text-[#1e3019] text-sm mb-1">{step.title}</p>
                      <p className="font-body text-[#1e3019]/50 text-xs sm:text-sm">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </FadeSection>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#f4f7f0] py-16 sm:py-24 px-4 sm:px-6 text-center">
        <FadeSection>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e3019] mb-6 sm:mb-8">
            Take Action for a Greener Future
          </h2>
          <p className="font-body text-[#1e3019]/60 text-base sm:text-lg max-w-2xl mx-auto mb-8 sm:mb-10">
            Carbonetics helps businesses stay ahead with AI-driven sustainability solutions. 🌿 Reduce emissions, optimize operations, and drive profitability with data-backed insights.
          </p>
          <Link to="/contact"
            className="inline-flex items-center gap-2 border border-[#1e3019] text-[#1e3019] font-body px-6 sm:px-8 py-3 sm:py-3.5 rounded-full hover:bg-[#1e3019] hover:text-white transition-all duration-300 text-sm sm:text-base">
            Get Free Consultation
          </Link>
        </FadeSection>
      </section>
    </main>
  );
}